// ============================================================================
// REGISTRATION TYPE MASTER SCREEN
// ============================================================================
// Master CRUD screen for basic.tbl_registration_type via prima-api
// /api/v1/RegistrationTypes.
//
// The per-jurisdiction list of statutory id types — PAN, GSTIN, CIN, TAN for
// India; BURS-TIN, BURS-VAT, OP-Number for Botswana; whatever the next
// jurisdiction brings. Client registrations point at these, which is why a
// type still in use cannot be deleted: the server answers 409 and the message
// says how many registrations hold it. Set it Inactive instead — existing
// registrations keep working and it stops being offered for new ones.

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  ActionMenu,
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  MinimizedModalStrip,
  ColumnSelector,
  type ColumnDefinition,
} from 'components/ui';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import notificationService from 'services/notificationService';
import {
  useRegistrationTypes,
  useCreateRegistrationType,
  useUpdateRegistrationType,
  useDeleteRegistrationType,
} from 'hooks/api/useRegistrationType';
import { useCountries } from 'hooks/api/useCountry';
import type {
  RegistrationType,
  RegistrationTypeSearchRequest,
  CreateRegistrationTypeRequest,
  UpdateRegistrationTypeRequest,
} from 'api/types';
import RegistrationTypeFormModal, {
  type RegistrationTypeFormData,
} from 'components/modals/modules/registration-type/RegistrationTypeFormModal';

type ViewMode = 'list' | 'grid';

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'ACTIVE', label: 'Active only' },
  { value: 'INACTIVE', label: 'Inactive only' },
];

const formatDate = (iso?: string | null): string => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '-';
  }
};

const apiErrorMessage = (err: any, fallback: string): string => {
  const apiBody = err?.response?.data;
  if (typeof apiBody?.message === 'string' && apiBody.message) return apiBody.message;
  if (Array.isArray(apiBody?.errors) && apiBody.errors.length) return apiBody.errors.join(', ');
  if (apiBody?.errors && typeof apiBody.errors === 'object') {
    return Object.values(apiBody.errors).flat().join(', ');
  }
  return err?.message || fallback;
};

interface RegistrationTypeMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  allowedActions?: string[];
}

const columnDefinitions: ColumnDefinition[] = [
  { id: 'code',             label: 'Code',      field: 'code',             required: true,  defaultVisible: true,  sortable: true },
  { id: 'name',             label: 'Name',      field: 'name',             required: true,  defaultVisible: true,  sortable: true },
  { id: 'country',          label: 'Country',   field: 'countryName',      required: false, defaultVisible: true,  sortable: false },
  { id: 'formatPattern',    label: 'Format',    field: 'formatPattern',    required: false, defaultVisible: true,  sortable: false },
  { id: 'issuingAuthority', label: 'Authority', field: 'issuingAuthority', required: false, defaultVisible: false, sortable: false },
  { id: 'usageCount',       label: 'In Use',    field: 'usageCount',       required: false, defaultVisible: true,  sortable: false },
  { id: 'status',           label: 'Status',    field: 'statusName',       required: false, defaultVisible: true,  sortable: true },
  { id: 'createdAt',        label: 'Created',   field: 'createdAt',        required: false, defaultVisible: false, sortable: true },
  { id: 'updatedAt',        label: 'Updated',   field: 'updatedAt',        required: false, defaultVisible: false, sortable: true },
];

const COLUMN_PREF_KEY = 'registration-type-master-columns';

const RegistrationTypeMasterScreen: React.FC<RegistrationTypeMasterScreenProps> = ({
  moduleIcon,
  tabId,
  moduleName,
  allowedActions = [],
}) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
    columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id)
  );

  useEffect(() => {
    const saved = localStorage.getItem(COLUMN_PREF_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) setVisibleColumns(parsed);
      } catch { /* ignore */ }
    }
  }, []);

  const handleApplyColumns = useCallback((cols: string[]) => {
    setVisibleColumns(cols);
    localStorage.setItem(COLUMN_PREF_KEY, JSON.stringify(cols));
  }, []);

  const handleResetColumns = useCallback(() => {
    const defaults = columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id);
    setVisibleColumns(defaults);
    localStorage.removeItem(COLUMN_PREF_KEY);
  }, []);

  // Server-side query state
  const [queryParams, setQueryParams] = useState<RegistrationTypeSearchRequest>({
    page: 1,
    pageSize: 25,
    includeInactive: true,
  });
  const { data, pagination, loading, refetch } = useRegistrationTypes(queryParams);
  const { data: countries } = useCountries();

  const countryOptions = useMemo(
    () =>
      (countries ?? []).map((c: any) => ({
        value: c.countryCode,
        label: `${c.countryCode} — ${c.countryName}`,
      })),
    [countries]
  );

  const countryFilterOptions = useMemo(
    () => [{ value: '', label: 'All countries' }, ...countryOptions],
    [countryOptions]
  );

  // View state
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  const [codeFilter, setCodeFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Search debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setQueryParams((prev) => ({
        ...prev,
        searchTerm: searchQuery || undefined,
        page: 1,
      }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams((prev) => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((size: number) => {
    setQueryParams((prev) => ({ ...prev, pageSize: size, page: 1 }));
  }, []);

  // Modal state
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedRecord, setSelectedRecord] = useState<RegistrationType | null>(null);
  const [isSavingForm, setIsSavingForm] = useState(false);
  const [formApiError, setFormApiError] = useState<string | null>(null);

  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const handleRestoreModal = useCallback(
    (restoredMode: 'create' | 'edit', instanceId?: string, recordCode?: string) => {
      if (instanceId) setCreateInstanceId(instanceId);
      if (recordCode) {
        const rec = data.find((r) => r.code === recordCode);
        if (rec) setSelectedRecord(rec);
      }
      setFormMode(restoredMode);
      setFormApiError(null);
      setFormModalOpen(true);
    },
    [data]
  );

  // Delete state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<RegistrationType | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Mutation hooks
  const { createRegistrationType } = useCreateRegistrationType();
  const { updateRegistrationType } = useUpdateRegistrationType();
  const { deleteRegistrationType } = useDeleteRegistrationType();

  const tableColumns: SyutGridColumn<RegistrationType>[] = useMemo(
    () => [
      { id: 'code', header: 'Code', accessorKey: 'code', size: 130, enableSorting: true },
      { id: 'name', header: 'Name', accessorKey: 'name', enableSorting: true },
      {
        id: 'country',
        header: 'Country',
        accessorKey: 'countryName',
        size: 170,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.countryCode
            ? `${row.original.countryCode} — ${row.original.countryName ?? ''}`.trim()
            : 'Applies everywhere',
      },
      {
        id: 'formatPattern',
        header: 'Format',
        accessorKey: 'formatPattern',
        size: 220,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.formatPattern ? (
            <span
              style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '0.75rem' }}
              title={row.original.formatPattern}
            >
              {row.original.formatPattern}
            </span>
          ) : (
            <span style={{ color: 'var(--text-secondary)' }}>Any</span>
          ),
      },
      {
        id: 'issuingAuthority',
        header: 'Authority',
        accessorKey: 'issuingAuthority',
        size: 200,
        enableSorting: false,
        cell: ({ row }) => row.original.issuingAuthority || '-',
      },
      {
        id: 'usageCount',
        header: 'In Use',
        accessorKey: 'usageCount',
        size: 90,
        enableSorting: false,
        cell: ({ row }) => row.original.usageCount ?? 0,
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'statusName',
        size: 110,
        enableSorting: true,
        cell: ({ row }) => {
          const isActive = row.original.statusCode === 'ACTIVE';
          return (
            <span className={`badge badge-${isActive ? 'active' : 'inactive'}`}>
              {row.original.statusName || (isActive ? 'Active' : 'Inactive')}
            </span>
          );
        },
      },
      {
        id: 'createdAt',
        header: 'Created',
        accessorKey: 'createdAt',
        size: 130,
        enableSorting: true,
        cell: ({ row }) => formatDate(row.original.createdAt),
      },
      {
        id: 'updatedAt',
        header: 'Updated',
        accessorKey: 'updatedAt',
        size: 130,
        enableSorting: true,
        cell: ({ row }) => formatDate(row.original.updatedAt),
      },
    ],
    []
  );

  const visibleTableColumns = useMemo<SyutGridColumn<RegistrationType>[]>(
    () => tableColumns.filter((col) => visibleColumns.includes(col.id as string)),
    [tableColumns, visibleColumns]
  );

  const handleAddNew = useCallback(() => {
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setSelectedRecord(null);
    setFormMode('create');
    setFormApiError(null);
    setFormModalOpen(true);
  }, []);

  const handleEdit = useCallback(
    (record: RegistrationType) => {
      const modalId = tabId ? `${tabId}-form-modal-edit-${record.code}` : null;
      if (modalId && isMinimized(modalId)) {
        const modal = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        modal?.onRestore?.();
        return;
      }
      setSelectedRecord(record);
      setFormMode('edit');
      setFormApiError(null);
      setFormModalOpen(true);
    },
    [tabId, isMinimized, dismissMinimized, getMinimizedModal]
  );

  const handleDeleteClick = useCallback((record: RegistrationType) => {
    setDeleteTarget(record);
    setDeleteModalOpen(true);
  }, []);

  const handleSaveForm = useCallback(
    async (formData: RegistrationTypeFormData) => {
      setIsSavingForm(true);
      setFormApiError(null);
      try {
        const common = {
          code: formData.code,
          name: formData.name,
          // Blank means "applies everywhere" — send null, not "".
          countryCode: formData.countryCode || null,
          description: formData.description || undefined,
          formatPattern: formData.formatPattern || null,
          issuingAuthority: formData.issuingAuthority || undefined,
          displayOrder: formData.displayOrder ? Number(formData.displayOrder) : null,
          statusCode: formData.statusCode,
        };

        if (formMode === 'create') {
          const payload: CreateRegistrationTypeRequest = common;
          await createRegistrationType(payload);
          notificationService.toast.success(`Registration type "${payload.code}" created.`);
        } else if (selectedRecord) {
          const payload: UpdateRegistrationTypeRequest = {
            ...common,
            rowVersion: selectedRecord.rowVersion,
          };
          await updateRegistrationType(selectedRecord.id, payload);
          notificationService.toast.success(`Registration type "${payload.code}" updated.`);
        }
        setIsSavingForm(false);
        setFormModalOpen(false);
        setSelectedRecord(null);
        refetch();
      } catch (err: any) {
        setIsSavingForm(false);
        const message = apiErrorMessage(err, 'Failed to save registration type. Please try again.');
        setFormApiError(message);
        notificationService.toast.error(message, 6000);
      }
    },
    [formMode, selectedRecord, createRegistrationType, updateRegistrationType, refetch]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteRegistrationType(deleteTarget.id);
      notificationService.toast.success(`Registration type "${deleteTarget.code}" deleted.`);
      setIsDeleting(false);
      setDeleteModalOpen(false);
      setDeleteTarget(null);
      refetch();
    } catch (err: any) {
      setIsDeleting(false);
      // The 409 body explains how many registrations still hold the type.
      notificationService.toast.error(
        apiErrorMessage(err, 'Failed to delete registration type.'),
        8000
      );
    }
  }, [deleteTarget, deleteRegistrationType, refetch]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (queryParams.code) count++;
    if (queryParams.name) count++;
    if (queryParams.countryCode) count++;
    if (queryParams.statusCode) count++;
    return count;
  }, [queryParams.code, queryParams.name, queryParams.countryCode, queryParams.statusCode]);

  const applyFilters = useCallback(() => {
    setQueryParams((prev) => ({
      ...prev,
      code: codeFilter || undefined,
      name: nameFilter || undefined,
      countryCode: countryFilter || undefined,
      statusCode: statusFilter || undefined,
      includeInactive: true,
      page: 1,
    }));
    setFilterOpen(false);
  }, [codeFilter, nameFilter, countryFilter, statusFilter]);

  const clearFilters = useCallback(() => {
    setCodeFilter('');
    setNameFilter('');
    setCountryFilter('');
    setStatusFilter('');
    setQueryParams((prev) => ({
      page: 1,
      pageSize: prev.pageSize,
      includeInactive: true,
      statusCode: undefined,
      searchTerm: searchQuery || undefined,
    }));
  }, [searchQuery]);

  // View modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewRecord, setViewRecord] = useState<RegistrationType | null>(null);

  const handleView = useCallback((record: RegistrationType) => {
    setViewRecord(record);
    setViewModalOpen(true);
  }, []);

  const getRowActions = useCallback(
    (row: RegistrationType): ActionMenuItem[] => {
      const items: ActionMenuItem[] = [];
      if (allowedActions.includes('view')) {
        items.push({
          id: 'view',
          label: 'View',
          icon: <IconSystem.eye size={16} />,
          onClick: () => handleView(row),
        });
      }
      if (allowedActions.includes('edit')) {
        items.push({
          id: 'edit',
          label: 'Edit',
          icon: <IconSystem.edit size={16} />,
          onClick: () => handleEdit(row),
        });
      }
      if (allowedActions.includes('delete')) {
        items.push({
          id: 'delete',
          label: 'Delete',
          icon: <IconSystem.trash size={16} />,
          onClick: () => handleDeleteClick(row),
          variant: 'danger',
          // Left enabled: the server owns the guard and its 409 message names
          // the usage count, which is more useful than a greyed-out item.
        });
      }
      return items;
    },
    [allowedActions, handleView, handleEdit, handleDeleteClick]
  );

  const actionButtons: ActionButton[] = useMemo(
    () => [
      ...(allowedActions.includes('add') ? [{
        id: 'add',
        icon: <IconSystem.add size={18} />,
        label: 'Add',
        tooltip: 'Add New Registration Type',
        onClick: handleAddNew,
        variant: 'icon' as const,
      }] : []),
      {
        id: 'refresh',
        icon: <IconSystem.refresh size={18} />,
        label: 'Refresh',
        tooltip: 'Refresh',
        onClick: () => refetch(),
        variant: 'icon' as const,
      },
    ],
    [allowedActions, handleAddNew, refetch]
  );

  const formInitialData: RegistrationTypeFormData | null = useMemo(() => {
    if (formMode !== 'edit' || !selectedRecord) return null;
    return {
      code: selectedRecord.code,
      name: selectedRecord.name,
      countryCode: selectedRecord.countryCode ?? '',
      description: selectedRecord.description ?? '',
      formatPattern: selectedRecord.formatPattern ?? '',
      issuingAuthority: selectedRecord.issuingAuthority ?? '',
      displayOrder:
        selectedRecord.displayOrder === null || selectedRecord.displayOrder === undefined
          ? ''
          : String(selectedRecord.displayOrder),
      statusCode: (selectedRecord.statusCode as 'ACTIVE' | 'INACTIVE') ?? 'ACTIVE',
    };
  }, [formMode, selectedRecord]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Registration Type Master"
        description="Statutory id types per jurisdiction: PAN, GSTIN, CIN, BURS-VAT, etc."
        icon={
          moduleIcon && (IconSystem as any)[moduleIcon]
            ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
            : <IconSystem.file size={18} />
        }
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search code, name, authority, country…',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: [
            { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List view' },
            { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid view' },
          ],
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen || activeFilterCount > 0,
          activeFilterCount,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
            visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {viewMode === 'list' && (
              <SyutGrid
                data={data}
                columns={visibleTableColumns}
                enableRowSelection
                enablePagination
                manualPagination
                totalCount={pagination.totalItems}
                currentPage={(queryParams.page ?? 1) - 1}
                pageSize={queryParams.pageSize ?? 25}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                enableSorting
                rowActions={
                  (allowedActions.includes('view') || allowedActions.includes('edit') || allowedActions.includes('delete'))
                    ? getRowActions
                    : undefined
                }
                emptyMessage={loading ? 'Loading registration types…' : 'No registration types found'}
              />
            )}

            {viewMode === 'grid' && (
              <div
                className="syut-grid-component"
                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
              >
                <div
                  style={{
                    padding: '1rem',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                    gap: '1.5rem',
                    flex: 1,
                    overflow: 'auto',
                    alignContent: 'start',
                  }}
                >
                  {data.length === 0 && (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                      {loading ? 'Loading registration types…' : 'No registration types found'}
                    </div>
                  )}
                  {data.map((item) => {
                    const isActive = item.statusCode === 'ACTIVE';
                    return (
                      <div
                        key={String(item.id)}
                        style={{
                          background: 'var(--bg-primary)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '8px',
                          padding: '1rem',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.5rem',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                              {item.countryCode ?? 'Any'} · {item.code}
                            </div>
                            <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0, marginBottom: '0.5rem' }}>
                              {item.name}
                            </h3>
                          </div>
                          <ActionMenu items={getRowActions(item)} />
                        </div>
                        {item.issuingAuthority && (
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                            {item.issuingAuthority}
                          </div>
                        )}
                        <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span className={`badge badge-${isActive ? 'active' : 'inactive'}`}>
                            {item.statusName || (isActive ? 'Active' : 'Inactive')}
                          </span>
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                            {item.usageCount ?? 0} in use
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {filterOpen && (
        <div className="search-panel">
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
              <IconSystem.x size={14} />
            </button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Code</label>
              <input type="text" className="form-input" value={codeFilter} onChange={(e) => setCodeFilter(e.target.value.toUpperCase())}
                placeholder="Filter by code…" style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Name</label>
              <input type="text" className="form-input" value={nameFilter} onChange={(e) => setNameFilter(e.target.value)}
                placeholder="Filter by name…" style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
            </div>
            <div className="filter-section">
              <SyutSelect label="Country" options={countryFilterOptions} value={countryFilter}
                onChange={(val) => setCountryFilter((val as string) || '')} isClearable={false} isSearchable size="sm" />
              <div style={{ marginTop: '0.25rem', fontSize: '0.6875rem', color: 'var(--text-secondary)' }}>
                Types that apply everywhere are always included
              </div>
            </div>
            <div className="filter-section">
              <SyutSelect label="Status" options={STATUS_FILTER_OPTIONS} value={statusFilter}
                onChange={(val) => setStatusFilter((val as string) || '')} isClearable={false} size="sm" />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
            <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
          </div>
        </div>
        )}
      </div>

      <RegistrationTypeFormModal
        isOpen={formModalOpen}
        mode={formMode}
        initialData={formInitialData}
        countryOptions={countryOptions}
        isSaving={isSavingForm}
        apiError={formApiError}
        onSave={handleSaveForm}
        onClose={() => {
          setFormModalOpen(false);
          setSelectedRecord(null);
          setFormApiError(null);
        }}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId}
        minimizeRef={formModalMinimizeRef}
      />

      <ConfirmDialog
        open={deleteModalOpen}
        title="Delete Registration Type"
        message={
          deleteTarget
            ? deleteTarget.usageCount > 0
              ? `"${deleteTarget.code} — ${deleteTarget.name}" is used by ${deleteTarget.usageCount} client registration(s). Deleting it will be refused — set it to Inactive instead.`
              : `Are you sure you want to delete "${deleteTarget.code} — ${deleteTarget.name}"? This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting…"
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (!isDeleting) {
            setDeleteModalOpen(false);
            setDeleteTarget(null);
          }
        }}
      />

      {viewModalOpen && viewRecord && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-container modal-md">
            <div className="modal-header" style={{ minHeight: 'auto' }}>
              <h2 className="modal-title">View Registration Type — {viewRecord.code}</h2>
              <button className="modal-close" onClick={() => { setViewModalOpen(false); setViewRecord(null); }} title="Close">
                <IconSystem.x size={20} />
              </button>
            </div>
            <div className="modal-content" style={{ fontSize: '0.8125rem' }}>
              <div className="form-grid-2">
                <div>
                  <label className="form-label">Code</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.code || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Name</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.name || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Country</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>
                    {viewRecord.countryCode ? `${viewRecord.countryCode} — ${viewRecord.countryName ?? ''}` : 'Applies everywhere'}
                  </div>
                </div>
                <div>
                  <label className="form-label">Issuing Authority</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.issuingAuthority || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Format Pattern</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)', fontFamily: 'var(--font-mono, monospace)', fontSize: '0.75rem', wordBreak: 'break-all' }}>{viewRecord.formatPattern || 'Any'}</div>
                </div>
                <div>
                  <label className="form-label">In Use</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.usageCount ?? 0} client registration(s)</div>
                </div>
                <div>
                  <label className="form-label">Description</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.description || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Status</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>
                    <span className={`badge badge-${viewRecord.statusCode === 'ACTIVE' ? 'active' : 'inactive'}`}>{viewRecord.statusName}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer" style={{ minHeight: 'auto' }}>
              <button className="btn btn-secondary" onClick={() => { setViewModalOpen(false); setViewRecord(null); }}>Close</button>
              {allowedActions.includes('edit') && (
                <button className="btn btn-primary" onClick={() => { setViewModalOpen(false); setViewRecord(null); handleEdit(viewRecord); }}>Edit</button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RegistrationTypeMasterScreen;
